import { useCallback, useState } from 'react';
import { CourseViewer } from '@/components/CourseViewer';
import { DebugOverlay } from '@/components/DebugOverlay';
import { useDirector } from '@/hooks/useDirector';
import { useDemoDirector } from '@/hooks/useDemoDirector';
import { useEventConfig } from '@/hooks/useEventConfig';
import { CourseScene } from '@/three/scenes/CourseScene';

// Check demo mode at module level
const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

// Backend mode component
function AppBackend() {
  const { config, loading, error } = useEventConfig('mock-event-1');
  const { queueManager, queueState, displayedShotIds, resetDirector, resetDirectorState } = useDirector();
  const [courseScene, setCourseScene] = useState<CourseScene | null>(null);

  const handleFullReset = useCallback(async () => {
    if (!config) return;
    resetDirector();
    resetDirectorState();
    courseScene?.clearAllShots();
    const defaultPreset = config.cameraPresets.default;
    if (defaultPreset) {
      courseScene?.setCameraPreset(defaultPreset, false);
    }
    console.log('[App] Full reset complete');
  }, [resetDirector, resetDirectorState, courseScene, config]);

  if (loading) {
    return (
      <div className="app loading">
        <div className="loading-message">
          <h2>Loading Event Configuration...</h2>
          <p>Please wait while we load the event data.</p>
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="app error">
        <div className="error-message">
          <h2>Failed to Load Event</h2>
          <p>{error?.message || 'Unknown error occurred'}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <CourseViewer
        eventConfig={config}
        queueManager={queueManager}
        queueState={queueState}
        onSceneReady={setCourseScene}
      />
      <DebugOverlay
        queueState={queueState}
        displayedShotIds={displayedShotIds}
        onReset={handleFullReset}
      />
    </div>
  );
}

// Demo mode component
function AppDemo() {
  const { config, loading, error } = useEventConfig('demo');
  const { queueManager, queueState, displayedShotIds, resetDirector, resetDirectorState } = useDemoDirector();
  const [courseScene, setCourseScene] = useState<CourseScene | null>(null);

  const handleFullReset = useCallback(async () => {
    if (!config) return;
    resetDirector();
    resetDirectorState();
    courseScene?.clearAllShots();
    const defaultPreset = config.cameraPresets.default;
    if (defaultPreset) {
      courseScene?.setCameraPreset(defaultPreset, false);
    }
    console.log('[App] Full reset complete (Demo Mode)');
  }, [resetDirector, resetDirectorState, courseScene, config]);

  if (loading) {
    return (
      <div className="app loading">
        <div className="loading-message">
          <h2>Loading Demo Configuration...</h2>
          <p>Please wait while we load the demo data.</p>
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="app error">
        <div className="error-message">
          <h2>Failed to Load Demo Config</h2>
          <p>{error?.message || 'Unknown error occurred'}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <CourseViewer
        eventConfig={config}
        queueManager={queueManager}
        queueState={queueState}
        onSceneReady={setCourseScene}
      />
      <DebugOverlay
        queueState={queueState}
        displayedShotIds={displayedShotIds}
        onReset={handleFullReset}
      />
      <a
        href="https://hce.studio"
        target="_blank"
        rel="noopener noreferrer"
        className="logo-link"
      >
        <img src="/images/HCE-Banner-on-dark.webp" alt="Human+Centred Engineering" />
      </a>
    </div>
  );
}

// Main App component - chooses which mode to render
function App() {
  console.log('[App] Running in', isDemoMode ? 'DEMO' : 'BACKEND', 'mode');
  return isDemoMode ? <AppDemo /> : <AppBackend />;
}

export default App;
